import Button from '@monofront/design-system/components/Button';
import Modal from '@monofront/design-system/organisms/Modal';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { ICheckoutProductError, IShoppingCart } from '@monofront/types';
import { useTranslations } from 'next-intl';

interface ICheckoutProductErrorsModalProps {
  isOpen: boolean;
  onValidate: () => void;
  onClose: () => void;
  checkoutProductErrors: ICheckoutProductError[];
  shoppingCart: IShoppingCart;
}
const CheckoutProductErrorsModal: React.FC<
  ICheckoutProductErrorsModalProps
> = ({ isOpen, onValidate, onClose, checkoutProductErrors, shoppingCart }) => {
  const tGlobal = useTranslations('Global');
  const tCatalog = useTranslations('Catalog');

  return (
    <>
      <Modal
        isOpen={isOpen}
        isClosable={false}
        isOverlayClosable
        centered
        hasPadding
        externalToggle={() => {
          onClose();
        }}
      >
        <div className="checkout-product-error-modal">
          <p className="h6 title">
            {/* product error can be any type of error, but we always handle it as an out of stock product message for customer */}
            {tCatalog.rich('outOfStock', {
              span: (children) => (
                <span aria-label={tGlobal('sadly')} role="img">
                  {children}
                </span>
              ),
            })}
          </p>
          <p>{tCatalog('notEnoughStock')}</p>
          <ul className="list">
            {checkoutProductErrors.map((checkoutProductError) => {
              const productDetails = shoppingCart.products.find(
                ({ sku }) => sku === checkoutProductError.sku,
              );

              if (!productDetails) return null;

              return (
                <li key={checkoutProductError.sku}>
                  <ResponsiveImage
                    src={productDetails.image || ''}
                    width={72}
                    height={72}
                  />
                  <div className="content">
                    <div className="hxrt">{productDetails.title}</div>
                    <div className="hxlt text-secondary">
                      {productDetails.vendor}
                    </div>
                    <div className="hxlt text-secondary">
                      {productDetails.weight}
                    </div>
                    <div className="hzb text-error">
                      {checkoutProductError.message}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <Button
            onClick={onClose}
            variant="secondary"
            dataTestId="CheckoutProductErrorsModal-Button-1"
          >
            {tCatalog('backToCart')}
          </Button>
          <Button
            onClick={onValidate}
            dataTestId="CheckoutProductErrorsModal-Button-continue-to-delivery"
          >
            {tCatalog('continueToDelivery')}
          </Button>
        </div>
      </Modal>
      <style jsx>{`
        .checkout-product-error-modal {
          max-width: 90vw;
          text-align: center;
          max-width: 420px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .title {
          margin-bottom: var(--space-1);
        }

        .list {
          background-color: var(--clr-secondary-3);
          margin: var(--space-5) 0;
          max-height: 220px;
          max-width: 100%;
          padding: var(--space-2);
          text-align: left;
          overflow: auto;
          border-radius: var(--rd-3);
        }

        li {
          display: flex;
          align-items: center;
          margin: var(--space-2);
        }

        .content {
          max-width: calc(100% - 72px);
        }

        .content > div {
          margin: var(--space-half) var(--space-2);
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .checkout-product-error-modal > :global(button:first-of-type) {
          margin-bottom: var(--space-2);
        }

        .checkout-product-error-modal > :global(button) {
          width: 249px;
          padding: 0;
        }
      `}</style>
    </>
  );
};

export default CheckoutProductErrorsModal;
